import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { CarregandoTela } from './features/layout/carregando-tela';
import { AppShell } from './features/layout/app-shell';
import { destinoDeEntrada } from './lib/destino';
import { useSessao } from './lib/usar-sessao';
import { ComRecurso } from './features/assinatura/com-recurso';

const Entrar = lazy(() => import('./rotas/entrar'));
const CriarConta = lazy(() => import('./rotas/criar-conta'));
const EscolherIgreja = lazy(() => import('./rotas/escolher-igreja'));
const EsqueciASenha = lazy(() => import('./rotas/recuperar-acesso'));
const LinkDeAcesso = lazy(() => import('./rotas/link-de-acesso'));
const NovaSenha = lazy(() => import('./rotas/nova-senha'));
const EntrarComLink = lazy(() => import('./rotas/entrar-com-link'));
const Inicio = lazy(() => import('./rotas/inicio'));
const Congregacoes = lazy(() => import('./rotas/congregacoes'));
const Pessoas = lazy(() => import('./rotas/pessoas'));
const FichaDaPessoa = lazy(() => import('./rotas/pessoa'));
const ImportarPessoas = lazy(() => import('./rotas/importar-pessoas'));
const Departamentos = lazy(() => import('./rotas/departamentos'));
const DetalheDoDepartamento = lazy(() => import('./rotas/departamento'));
const MinhaDisponibilidade = lazy(() => import('./rotas/minha-disponibilidade'));
const AgendaDaIgreja = lazy(() => import('./rotas/agenda'));
const DetalheDoEvento = lazy(() => import('./rotas/evento'));
const Escalas = lazy(() => import('./rotas/escalas'));
const MinhasEscalas = lazy(() => import('./rotas/minhas-escalas'));
const PainelDoDia = lazy(() => import('./rotas/painel-do-dia'));
const Confirmar = lazy(() => import('./rotas/confirmar'));
const Convite = lazy(() => import('./rotas/convite'));
const Checkin = lazy(() => import('./rotas/checkin'));
const Comunicados = lazy(() => import('./rotas/comunicados'));
const Notificacoes = lazy(() => import('./rotas/notificacoes'));
const Auditoria = lazy(() => import('./rotas/auditoria'));
const WhatsApp = lazy(() => import('./rotas/whatsapp'));
const Repertorio = lazy(() => import('./rotas/repertorio'));
const FichaDaMusica = lazy(() => import('./rotas/musica'));
const Roteiro = lazy(() => import('./rotas/roteiro'));
const Apresentacao = lazy(() => import('./rotas/apresentacao'));
const Kids = lazy(() => import('./rotas/kids'));
const FichaDaCrianca = lazy(() => import('./rotas/crianca'));
const SalasDoKids = lazy(() => import('./rotas/salas-do-kids'));
const HabilitacoesDoKids = lazy(() => import('./rotas/habilitacoes-do-kids'));
const EstacaoDoKids = lazy(() => import('./rotas/estacao-do-kids'));
const SalaDoKids = lazy(() => import('./rotas/sala-do-kids'));
const MeusFilhos = lazy(() => import('./rotas/meus-filhos'));
const Relatorios = lazy(() => import('./rotas/relatorios'));
const AssinaturaDaIgreja = lazy(() => import('./rotas/assinatura'));
const Backoffice = lazy(() => import('./rotas/backoffice'));
const Acessibilidade = lazy(() => import('./rotas/acessibilidade'));
const Seguranca = lazy(() => import('./rotas/seguranca'));
const DadosDaIgreja = lazy(() => import('./rotas/dados-da-igreja'));
const Ajuda = lazy(() => import('./rotas/ajuda'));

function Protegida({ children }: { children: React.ReactNode }) {
  const { carregando, usuario, igreja } = useSessao();
  const local = useLocation();

  if (carregando) return <CarregandoTela />;
  if (!usuario) return <Navigate to="/entrar" replace state={{ de: local.pathname }} />;
  // Uma pessoa pode servir em mais de uma igreja: sem igreja escolhida, não há
  // contexto de trabalho.
  if (!igreja) return <Navigate to="/escolher-igreja" replace />;

  return <AppShell>{children}</AppShell>;
}

/**
 * A área da El Shaday (M17).
 *
 * Ela é o **único** lugar do produto que não exige igreja escolhida: quem dá
 * suporte não pertence a igreja nenhuma, e é justamente isso que faz da
 * impersonação auditada o único caminho para dentro de uma conta.
 */
function SomentePlataforma({ children }: { children: React.ReactNode }) {
  const { carregando, usuario } = useSessao();
  const local = useLocation();

  if (carregando) return <CarregandoTela />;
  if (!usuario) return <Navigate to="/entrar" replace state={{ de: local.pathname }} />;

  return <AppShell>{children}</AppShell>;
}

/**
 * Telas de quem ainda não entrou.
 *
 * Elas **não** esperam a checagem de sessão para desenhar. Quem abre `/entrar`
 * quase sempre não tem sessão, e segurar a tela por uma ida ao servidor atrasa
 * a primeira pintura justamente para quem mais sofre com isso: celular antigo,
 * rede ruim (seção 9.5). Se houver sessão, o redirecionamento acontece assim
 * que ela é conhecida.
 */
function SomenteVisitante({ children }: { children: React.ReactNode }) {
  const { carregando, usuario, igreja } = useSessao();
  if (!carregando && usuario) return <Navigate to={destinoDeEntrada(usuario, igreja)} replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<CarregandoTela />}>
        <Routes>
          <Route
            path="/entrar"
            element={
              <SomenteVisitante>
                <Entrar />
              </SomenteVisitante>
            }
          />
          <Route
            path="/criar-conta"
            element={
              <SomenteVisitante>
                <CriarConta />
              </SomenteVisitante>
            }
          />
          {/*
            * A porta de volta (ADR 0016). Pedir o link é coisa de quem está de
            * fora, então vale a mesma guarda da tela de entrar. Gastar o link,
            * não: quem tem uma sessão velha e clica no link do e-mail precisa
            * que ele funcione, e não ser mandado de volta para dentro.
            */}
          <Route
            path="/esqueci-a-senha"
            element={
              <SomenteVisitante>
                <EsqueciASenha />
              </SomenteVisitante>
            }
          />
          <Route
            path="/link-de-acesso"
            element={
              <SomenteVisitante>
                <LinkDeAcesso />
              </SomenteVisitante>
            }
          />
          <Route path="/nova-senha/:token" element={<NovaSenha />} />
          <Route path="/entrar/link/:token" element={<EntrarComLink />} />
          <Route path="/escolher-igreja" element={<EscolherIgreja />} />
          <Route
            path="/inicio"
            element={
              <Protegida>
                <Inicio />
              </Protegida>
            }
          />
          <Route
            path="/pessoas"
            element={
              <Protegida>
                <Pessoas />
              </Protegida>
            }
          />
          <Route
            path="/pessoas/importar"
            element={
              <Protegida>
                <ImportarPessoas />
              </Protegida>
            }
          />
          <Route
            path="/pessoas/:id"
            element={
              <Protegida>
                <FichaDaPessoa />
              </Protegida>
            }
          />
          <Route
            path="/agenda"
            element={
              <Protegida>
                <AgendaDaIgreja />
              </Protegida>
            }
          />
          <Route
            path="/agenda/:id"
            element={
              <Protegida>
                <DetalheDoEvento />
              </Protegida>
            }
          />
          <Route
            path="/minhas-escalas"
            element={
              <Protegida>
                <MinhasEscalas />
              </Protegida>
            }
          />
          <Route
            path="/repertorio"
            element={
              <ComRecurso recurso="roteiro">
                <Protegida>
                  <Repertorio />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/repertorio/:id"
            element={
              <ComRecurso recurso="roteiro">
                <Protegida>
                  <FichaDaMusica />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/agenda/:id/roteiro"
            element={
              <ComRecurso recurso="roteiro">
                <Protegida>
                  <Roteiro />
                </Protegida>
              </ComRecurso>
            }
          />
          {/* Sem AppShell: a tela é o telão dos bastidores. */}
          <Route
            path="/agenda/:id/apresentacao"
            element={
              <Protegida>
                <Apresentacao />
              </Protegida>
            }
          />
          <Route
            path="/agenda/:id/dia"
            element={
              <Protegida>
                <PainelDoDia />
              </Protegida>
            }
          />
          {/* Sem AppShell: o link chega por mensagem e não exige login. */}
          <Route path="/confirmar/:token" element={<Confirmar />} />
          <Route path="/convite/:token" element={<Convite />} />
          <Route
            path="/checkin/:id"
            element={
              <Protegida>
                <Checkin />
              </Protegida>
            }
          />
          <Route
            path="/escalas"
            element={
              <Protegida>
                <Escalas />
              </Protegida>
            }
          />
          <Route
            path="/kids"
            element={
              <ComRecurso recurso="kids">
                <Protegida>
                  <Kids />
                </Protegida>
              </ComRecurso>
            }
          />
          {/* Antes de `/kids/:id`, senão "salas" viraria o id de uma criança. */}
          <Route
            path="/kids/salas"
            element={
              <ComRecurso recurso="kids">
                <Protegida>
                  <SalasDoKids />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/kids/estacao"
            element={
              <ComRecurso recurso="kids">
                <Protegida>
                  <EstacaoDoKids />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/kids/sala/:eventId"
            element={
              <ComRecurso recurso="kids">
                <Protegida>
                  <SalaDoKids />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/kids/meus-filhos"
            element={
              <ComRecurso recurso="kids">
                <Protegida>
                  <MeusFilhos />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/kids/habilitacoes"
            element={
              <ComRecurso recurso="kids">
                <Protegida>
                  <HabilitacoesDoKids />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/kids/:id"
            element={
              <ComRecurso recurso="kids">
                <Protegida>
                  <FichaDaCrianca />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/departamentos"
            element={
              <Protegida>
                <Departamentos />
              </Protegida>
            }
          />
          <Route
            path="/departamentos/:id"
            element={
              <Protegida>
                <DetalheDoDepartamento />
              </Protegida>
            }
          />
          <Route
            path="/minha-disponibilidade"
            element={
              <Protegida>
                <MinhaDisponibilidade />
              </Protegida>
            }
          />
          <Route
            path="/comunicados"
            element={
              <Protegida>
                <Comunicados />
              </Protegida>
            }
          />
          <Route
            path="/relatorios"
            element={
              <ComRecurso recurso="relatorios">
                <Protegida>
                  <Relatorios />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/backoffice"
            element={
              <SomentePlataforma>
                <Backoffice />
              </SomentePlataforma>
            }
          />
          <Route
            path="/ajuda"
            element={
              <Protegida>
                <Ajuda />
              </Protegida>
            }
          />
          <Route
            path="/ajuda/:slug"
            element={
              <Protegida>
                <Ajuda />
              </Protegida>
            }
          />
          <Route
            path="/configuracoes/acessibilidade"
            element={
              <Protegida>
                <Acessibilidade />
              </Protegida>
            }
          />
          <Route
            path="/configuracoes/seguranca"
            element={
              <Protegida>
                <Seguranca />
              </Protegida>
            }
          />
          <Route
            path="/configuracoes/igreja"
            element={
              <Protegida>
                <DadosDaIgreja />
              </Protegida>
            }
          />
          <Route
            path="/assinatura"
            element={
              <Protegida>
                <AssinaturaDaIgreja />
              </Protegida>
            }
          />
          <Route
            path="/configuracoes/notificacoes"
            element={
              <Protegida>
                <Notificacoes />
              </Protegida>
            }
          />
          <Route
            path="/configuracoes/congregacoes"
            element={
              <Protegida>
                <Congregacoes />
              </Protegida>
            }
          />
          <Route
            path="/configuracoes/whatsapp"
            element={
              <ComRecurso recurso="whatsapp">
                <Protegida>
                  <WhatsApp />
                </Protegida>
              </ComRecurso>
            }
          />
          <Route
            path="/configuracoes/auditoria"
            element={
              <Protegida>
                <Auditoria />
              </Protegida>
            }
          />
          <Route path="/" element={<Navigate to="/inicio" replace />} />
          <Route path="*" element={<Navigate to="/inicio" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
